import { IMAGES } from "./images";

// Deterministic generator for apartment layouts per building.
function genApartments(building, prefix, count, perFloor) {
  const list = [];
  const profiles = [
    { rooms: 1, area: 34.5, terrace: 6.2 },
    { rooms: 2, area: 52.8, terrace: 9.4 },
    { rooms: 3, area: 71.3, terrace: 12.6 },
    { rooms: 4, area: 88.7, terrace: 15.1 },
    { rooms: 2, area: 58.1, terrace: 10.2 },
  ];
  for (let n = 1; n <= count; n++) {
    const floor = Math.floor((n - 1) / perFloor) + 1;
    const pos = (n - 1) % perFloor;
    const p = profiles[pos % profiles.length];
    const area = +(p.area + (floor - 3) * 0.6).toFixed(2);
    const orientations = ["Pietūs / Vakarai", "Šiaurė / Rytai", "Vakarai", "Rytai", "Pietūs"];
    list.push({
      code: `${building} ${prefix}${n}`,
      building,
      number: n,
      unit: `${prefix}${n}`,
      floor,
      floors: 5,
      rooms: p.rooms,
      area,
      terrace: p.terrace,
      terraceType: "loggia",
      orientation: orientations[pos % orientations.length],
      pricePerM2: 2478,
      price: Math.round(area * 2478),
      sheet: IMAGES.apartmentSheets[`${building} ${prefix}${n}`] || "",
      photos: IMAGES.apartmentPhotos[`${building} ${prefix}${n}`] || [],
    });
  }
  return list;
}

// Migdolas — explicit unit data: [no, rooms, terraceType, area, floor, price, status]
const MIGDOLAS_DATA = [
  [1, 3, "terrace", 49.56, 1, 132966, "Reserved"],
  [2, 4, "terrace", 71.42, 1, 189837, "Available"],
  [3, 3, "terrace", 71.42, 1, 189837, "Available"],
  [4, 2, "terrace", 49.56, 1, 132966, "Available"],
  [5, 2, "loggia", 55.36, 2, 127486, "Available"],
  [6, 3, "loggia", 85.32, 2, 194247, "Available"],
  [7, 3, "loggia", 85.32, 2, 194247, "Sold"],
  [8, 4, "loggia", 55.4, 2, 127486, "Available"],
  [9, 4, "loggia", 55.4, 3, 127486, "Sold"],
  [10, 6, "loggia", 85.3, 3, 194247, "Available"],
  [11, 6, "loggia", 85.3, 3, 194247, "Available"],
  [12, 4, "loggia", 55.4, 3, 127486, "Available"],
  [13, 4, "loggia", 55.4, 4, 127486, "Sold"],
  [14, 6, "loggia", 85.3, 4, 194247, "Reserved"],
  [15, 6, "loggia", 85.3, 4, 194247, "Available"],
  [16, 4, "loggia", 55.4, 4, 127486, "Available"],
  [17, 2, "loggia", 49.4, 5, 124641, "Available"],
  [18, 4, "loggia", 83.7, 5, 212491, "Available"],
  [19, 4, "loggia", 83.7, 5, 212491, "Reserved"],
  [20, 2, "loggia", 49.4, 5, 124641, "Reserved"],
];

// Extra table specs per unit (only populated where confirmed data exists).
// orientationKeys use direction keys resolved via t(`spec.dir_${key}`).
const MIGDOLAS_EXTRA = {
  A1: { apartmentArea: 49.56, orientationKeys: ["south", "east"], terraceArea: 15, landPlotArea: 3049, landShare: 145 },
  A2: { apartmentArea: 71.42, orientationKeys: ["southwest"], terraceArea: 20, landShare: 72 },
  A3: { apartmentArea: 71.42, orientationKeys: ["northwest"], terraceArea: 20, landShare: 79 },
  A4: { apartmentArea: 49.56, orientationKeys: ["northeast"], terraceArea: 15, landShare: 146 },
  A5: { apartmentArea: 49.56, orientationKeys: ["southeast"], loggiaArea: 5.8, factsVariant: "loggia" },
  A6: { apartmentArea: 71.42, orientationKeys: ["southwest"], loggiaArea: 13.9, factsVariant: "loggia" },
  A7: { apartmentArea: 71.42, orientationKeys: ["northwest"], loggiaArea: 13.9, factsVariant: "loggia" },
};

export const MIGDOLAS = MIGDOLAS_DATA.map(([n, rooms, terraceType, area, floor, price, status]) => {
  const extra = MIGDOLAS_EXTRA[`A${n}`] || {};
  return {
    code: `Migdolas A${n}`,
    building: "Migdolas",
    number: n,
    unit: `A${n}`,
    floor,
    floors: 5,
    rooms,
    terraceType,
    terrace: null,
    area,
    orientation: n === 20 ? "Šiaurė / Rytai" : "—",
    price,
    pricePerM2: Math.round(price / area),
    defaultStatus: status,
    featured: n === 20,
    apartmentArea: extra.apartmentArea ?? null,
    orientationKeys: extra.orientationKeys ?? [],
    terraceArea: extra.terraceArea ?? null,
    landPlotArea: extra.landPlotArea ?? null,
    landShare: extra.landShare ?? null,
    loggiaArea: extra.loggiaArea ?? null,
    factsVariant: extra.factsVariant ?? null,
    sheet: IMAGES.apartmentSheets[`Migdolas A${n}`] || "",
    photos: IMAGES.apartmentPhotos[`Migdolas A${n}`] || [],
  };
});

export const VYSNIA = genApartments("Vyšnia", "B", 25, 5);

export const BUILDINGS = {
  Migdolas: {
    key: "Migdolas",
    apartments: MIGDOLAS,
    totalApartments: 20,
    floors: 5,
    perFloor: 4,
    floorPlans: IMAGES.migdolasFloors,
  },
  Vyšnia: {
    key: "Vyšnia",
    apartments: VYSNIA,
    totalApartments: 25,
    floors: 5,
    perFloor: 5,
    floorPlans: IMAGES.vysniaFloors,
  },
};

export const APARTMENT_FEATURES = [
  { lt: "Blokinis pastatas", en: "Block-built", ru: "Блочный дом" },
  { lt: "Centrinis + elektrinis šildymas", en: "Central + electric heating", ru: "Центральное + электрическое отопление" },
  { lt: "Energinė klasė A++", en: "Energy class A++", ru: "Класс энергоэффективности A++" },
  { lt: "Liftas", en: "Elevator", ru: "Лифт" },
  { lt: "Aukštos lubos", en: "High ceilings", ru: "Высокие потолки" },
  { lt: "Nauja santechnika ir elektra", en: "New plumbing & electrics", ru: "Новая сантехника и электрика" },
  { lt: "Paruošta internetui", en: "Internet-ready", ru: "Готово к интернету" },
  { lt: "Atvira virtuvė-svetainė", en: "Open kitchen-living", ru: "Открытая кухня-гостиная" },
  { lt: "Terasa", en: "Terrace", ru: "Терраса" },
  { lt: "Oro kondicionierius", en: "Air conditioning", ru: "Кондиционер" },
  { lt: "Grindinis šildymas", en: "Underfloor heating", ru: "Тёплый пол" },
  { lt: "Plastikiniai vamzdžiai", en: "Plastic pipes", ru: "Пластиковые трубы" },
  { lt: "Rekuperacinė sistema", en: "Heat recovery (recuperation)", ru: "Система рекуперации" },
  { lt: "Šarvuotos durys", en: "Armored door", ru: "Бронированная дверь" },
  { lt: "Signalizacija", en: "Alarm system", ru: "Сигнализация" },
  { lt: "Kodinė laukujė spyna", en: "Coded entrance lock", ru: "Кодовый замок на входе" },
  { lt: "Vaizdo stebėjimas", en: "Video surveillance", ru: "Видеонаблюдение" },
];

export const CONTACT = {
  phone: "+37063610123",
  phoneDisplay: "+370 636 10123",
  email: "sakurunamai@gmail.com",
  whatsapp: "34601655388",
  instagram: "https://www.instagram.com/sakuru_namai",
  facebook: "https://www.facebook.com/profile.php?id=61591464343781",
  address: "Parko g. 1D, Marijampolė",
  mapLat: 54.5706252,
  mapLon: 23.3599208,
};


// ---- "More about the apartment" spec config ----
export const PROJECT_SPEC_DEFAULTS = {
  yearBuilt: 2026,
  finishing: "partial",
  buildingType: "block",
  heating: "centralElectric",
  energyClass: "A++",
  features: [
    "elevator", "separateEntrance", "highCeilings", "newSewage",
    "newWiring", "enclosedYard", "internet", "kitchenLiving",
  ],
  equipment: ["airConditioning", "underfloorHeating", "plasticPipes", "heatRecovery"],
  security: ["armoredDoor", "alarm", "codedLock", "cctv"],
};

// Per-apartment overrides (orientation as direction keys, etc.)
export const APARTMENT_SPEC_OVERRIDES = {
  "Migdolas A1": { orientation: ["south", "east"], terraceArea: 15 },
  "Migdolas A2": {
    orientation: ["southwest"],
    terraceArea: 20,
    equipment: ["airConditioning", "underfloorHeating", "plasticPipes"],
  },
  "Migdolas A3": {
    orientation: ["northwest"],
    terraceArea: 20,
    features: ["separateEntrance", "highCeilings", "newSewage", "newWiring", "enclosedYard", "internet", "kitchenLiving"],
    equipment: ["airConditioning", "underfloorHeating", "plasticPipes"],
  },
  "Migdolas A4": {
    orientation: ["northeast"],
    terraceArea: 15,
    features: ["highCeilings", "newSewage", "newWiring", "internet", "kitchenLiving"],
  },
  "Migdolas A5": {
    orientation: ["southeast"],
    apartmentArea: 49.56,
    terraceArea: 5.8,
    features: ["elevator", "highCeilings", "newSewage", "newWiring", "internet", "kitchenLiving"],
  },
  "Migdolas A6": {
    orientation: ["southwest"],
    apartmentArea: 71.42,
    terraceArea: 13.9,
    features: ["elevator", "highCeilings", "newSewage", "newWiring", "internet", "kitchenLiving"],
  },
  "Migdolas A7": {
    orientation: ["northwest"],
    apartmentArea: 71.42,
    terraceArea: 13.9,
    features: ["elevator", "highCeilings", "newSewage", "newWiring", "internet", "kitchenLiving"],
  },
};

export function getApartmentSpec(apt) {
  const o = apt._specOverride || APARTMENT_SPEC_OVERRIDES[apt.code] || {};
  return {
    ...PROJECT_SPEC_DEFAULTS,
    ...o,
    rooms: apt.rooms,
    area: apt.area,
    floor: apt.floor,
    totalFloors: apt.floors,
    additionalSpaces: [apt.terraceType === "terrace" ? "terrace" : "loggia"],
    orientation: o.orientation || [],
    terraceArea: o.terraceArea,
    apartmentArea: o.apartmentArea,
  };
}
